import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import Modal from "../../Modal";
import TextInput from "../inputs/TextInput";
import { socket } from "../../../socket";
import { useSocket } from "../../../contexts/SocketContext";
import { characters, characterSkinColours, userColours } from "../../../assets/general";
import { Canvas, ObjectMap, useThree } from "@react-three/fiber";
import { Html, OrbitControls, useGLTF, useTexture } from "@react-three/drei";
import { GLTF, OrbitControls as OrbitType } from 'three-stdlib'
import { Mesh, SRGBColorSpace, Texture } from "three";
import { CharacterSlugStrings, CharacterSkinStrings } from "../../../types/SharedTypes";
import { ErrorBoundary } from "react-error-boundary";
import LoadingContainer from "../LoadingContainer";
import ErrorBoundaryFallback from "../ErrorBoundaryFallback";

type Props = {
  setModalVisible: (prev: boolean) => void;
}

const EditUserModal = ({setModalVisible}: Props) => {

  const {user} = useSocket();

  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [colour, setColour] = useState('');

  const disableConfirm = name.length === 0 || colour.length === 0;

  const handleUpdate = () => {
    if (!user) return;
    setIsLoading(true);
    socket.emit('updateUser', {userId: user.id, name, colour});
  }

  useEffect(() => {

    const handleUpdateDone = () => {
      setIsLoading(false);
    }

    socket.on("updatedUser", handleUpdateDone);

    return () => {
      socket.off("updatedUser", handleUpdateDone);
    }
  }, []);

  return (
    <Modal
      fixedBottomItem
      bottomPane
      setModalVisible={setModalVisible}
      controls
      isLoading={isLoading}
      modalTitle={'Wait a sec'}
      children={
        <></>
      }
      fixedBottomItemChildren={
        <div className="item-list padding-small">
          <button aria-disabled={disableConfirm} disabled={disableConfirm} className="primary" onClick={handleUpdate}>{"Save"}</button>
          <button className={"secondary"} onClick={() => setModalVisible(false)}>Cancel</button>
        </div>
      }
    />
  )
}

export const EditUserForm = () => {

  const {user} = useSocket();

  const [isLoading, setIsLoading] = useState(false);

  const [name, setName] = useState(user?.name || '');
  const [colour, setColour] = useState(user?.colour || userColours['red']);
  const [selectedCharacter, setSelecterCharacter] = useState<CharacterSlugStrings>(user?.character?.slug || "char2");
  const [selectedSkin, setSelectedSkin] = useState<CharacterSkinStrings>(user?.character?.skin || "medium");

  const [characterModalVisible, setCharacterModalVisible] = useState(false);

  const disableConfirm = 
    name.length === 0 || 
    colour.length === 0 || 
    (name === user?.name && colour === user.colour && selectedCharacter === user.character.slug && selectedSkin === user.character.skin);

  const handleUpdate = () => {
    if (!user) return;
    setIsLoading(true);
    socket.emit('updateUser', {userId: user.id, name, colour, character: {slug: selectedCharacter, skin: selectedSkin}});
  }

  useEffect(() => {

    const handleUpdateDone = () => {
      setIsLoading(false);
    }

    socket.on("updatedUser", handleUpdateDone);

    return () => {
      socket.off("updatedUser", handleUpdateDone);
    }
  }, []);

  return (
    <>
      {characterModalVisible && 
        <SelectCharacterModal
          setModalVisible={setCharacterModalVisible}
          selectedCharacter={selectedCharacter}
          setSelectedCharacter={setSelecterCharacter}
          selectedSkin={selectedSkin}
          setSelectedSkin={setSelectedSkin}
        />
      }
      <div className="item-list">
        <p className="subheading-small center">Player</p>
        <div className="item-row large-gap wrap center-vertical center-horizontal">
          <div className="user-avatar large outline" role="button" onClick={() => setCharacterModalVisible(true)}>
            <img src={`img/characters/${characters[selectedCharacter].name}-${selectedSkin}.png`} />
          </div>
          <div className="item-list">
            <TextInput
              id={'username'}
              value={name}
              onChange={(value) => setName(value)}
              secondary
              placeholder="SuperAligator47"
            />
            <div className="item-row toggle-buttons">
              {Object.entries(userColours).map(clr =>
                <button
                  key={clr[0]}
                  aria-current={colour === clr[0]}
                  style={{backgroundColor: clr[1]}}
                  onClick={() => setColour(clr[0])}
                />
              )}
            </div>
          </div>
        </div>
        <div className="item-list padding-small center-vertical center-horizontal">
          <button
            aria-disabled={disableConfirm}
            disabled={disableConfirm}
            className="primary"
            onClick={handleUpdate}
          >
            Save
          </button>
        </div>
      </div>
    </>
  )
}

type CharacterModalProps = Props & {
  selectedCharacter: CharacterSlugStrings;
  setSelectedCharacter: (prev: CharacterSlugStrings) => void;
  selectedSkin: CharacterSkinStrings;
  setSelectedSkin: (prev: CharacterSkinStrings) => void;
}

const SelectCharacterModal = ({
  setModalVisible,
  selectedCharacter,
  setSelectedCharacter,
  selectedSkin,
  setSelectedSkin
}: CharacterModalProps) => {

  const charactersArray = Object.entries(characters);

  function Loader() {
    return <Html center><p className='heading-large'>Loading…</p></Html>;
  }

  return (
    <Modal
      setModalVisible={setModalVisible}
      modalTitle={"Select Character"}
    >
      <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
        <div className="position-relative item-row center-horizontal">
          <Canvas
            camera={{fov: 65}}
            frameloop="demand"
          >
            <Suspense fallback={<Loader />}>
              <RenderCharacter slug={selectedCharacter} skinSlug={selectedSkin} />
            </Suspense>
          </Canvas>
          <div className="tile round padding-xs padding-left item-row position-absolute absolute-top-large large-gap">
            <p className="subheading-large weight-light header secondary">
              {characters[selectedCharacter].name}
            </p>
            <button className="primary short" onClick={() => setModalVisible(false)}>Select</button>
          </div>
          <div className="item-list position-absolute center-horizontal absolute-bottom-large full-width">
            <div className="tile item-row overlay padding-small">
              {Object.entries(characterSkinColours).map(skin =>
                <button
                  key={skin[0]}
                  className="icon large outline"
                  title={skin[0]}
                  style={{background: skin[1]}}
                  onClick={() => setSelectedSkin(skin[0] as CharacterSkinStrings)}
                >
                </button>
              )}
            </div>
            <div className="tile overlay padding-small-horizontal">
              <div className="filter-buttons">
                <div className="filter-buttons-row item-row">
                  {charactersArray.map(char =>
                    <button
                      key={char[0]}
                      className="icon outline no-padding"
                      title={char[0]}
                      onClick={() => setSelectedCharacter(char[0] as CharacterSlugStrings)}
                    >
                      <div className="user-avatar med">
                        <img src={`img/characters/${char[1].name}-${selectedSkin}.png`} />
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    </Modal>
  )
}

const RenderCharacter = ({slug, skinSlug}: {slug: CharacterSlugStrings, skinSlug: CharacterSkinStrings}) => {

  const {invalidate} = useThree();

  let obj: GLTF & ObjectMap;
  let texture: Texture;
  try {
    obj = useGLTF(`/3d/${slug}/scene.gltf`);
    texture = useTexture(`/3d/${slug}/textures/texture-${skinSlug}.png`);
  }
  catch (err) {
    throw Error('idk');
  }

  const object = useMemo(() => obj.scene.clone(true), [slug]);
  const orbitRef = useRef<OrbitType>(null);

  const configuredTexture = useMemo(() => {
    texture.colorSpace = SRGBColorSpace;
    texture.flipY = false;
    return texture;
  }, [texture]);

  useEffect(() => {
    if (!orbitRef.current) return;
    orbitRef.current.setAzimuthalAngle(0);
    orbitRef.current.setPolarAngle(1.3);
  }, [slug]);

  //these are sparated incase the material needs to be changed again
  //since cloning is a lot more expensive
  //but the texture also doesn't change again so unnecessary
  useEffect(() => {
    object.traverse(child => {
      if (child instanceof Mesh) {
        child.material = child.material.clone();
      }
    });
  }, [object]);

  useEffect(() => {
    object.traverse(child => {
      if (child instanceof Mesh) {
        child.material.map = configuredTexture;
      }
    });
    invalidate();
  }, [object, configuredTexture]);

  return (
    <>
      <ambientLight intensity={1} color={"white"} />
      <primitive object={object} scale={characters[slug].scale * 1.1} position={[0,characters[slug].previewHeight,0]} />
      <OrbitControls
        ref={orbitRef}
        enableZoom={false}
        enablePan={false}
        minAzimuthAngle={-Math.PI / 5}
        maxAzimuthAngle={Math.PI / 5}
        minPolarAngle={Math.PI / 4}
        maxPolarAngle={Math.PI - Math.PI / 2}
      />
    </>
  )
}

export default EditUserModal;